export type LogLevel = 'debug' | 'info' | 'warn' | 'error'

export interface LogEntry {
  level: LogLevel
  space: string
  message: string
  data?: object
}

export type LogHandler = (entry: LogEntry) => void

export interface LogOptions {
  handler?: LogHandler
  space: string
}

function consoleLogHandler({ level, message, data }: LogEntry) {
  // eslint-disable-next-line no-console
  const logFunc = console[level] || console.log
  logFunc.apply(console, data ? [message, data] : [message])
}

export class Log {
  /**
   * The default log instance, logging to the console with the `global` namespace.
   */
  static readonly default = new this({
    space: 'global',
    handler: consoleLogHandler,
  })

  _handler?: LogHandler
  _space: string
  _type: string = 'log'

  /**
   * Creates a new Log instance.
   *
   * @param options Options for the logger.
   */
  constructor(options?: LogOptions) {
    this._handler = options?.handler || Log.default._handler
    this._space = options?.space || Log.default._space
  }

  /**
   * Logs a debug message.
   *
   * @param message The debug message.
   * @param data Optional additional data.
   */
  debug(message: string, data?: any) {
    this.write('debug', message, data)
  }

  /**
   * Logs an informational message.
   *
   * @param message The informational message.
   * @param data Optional additional data.
   */
  info(message: string, data?: any) {
    this.write('info', message, data)
  }

  /**
   * Logs a warning message.
   *
   * @param message The warning message.
   * @param data Optional additional data.
   */
  warn(message: string, data?: any) {
    this.write('warn', message, data)
  }

  /**
   * Logs an error message.
   *
   * @param message The error message or an Error object.
   * @param data Optional additional data.
   */
  error(message: string | Error, data?: any) {
    const error = message instanceof Error ? message : null
    if (error) {
      data = {
        error: message,
        ...data,
      }
    }
    // @ts-expect-error - message is string or Error
    this.write('error', error?.message ?? message, data)
  }

  /**
   * Writes a log entry.
   *
   * @param level The log level.
   * @param message The log message.
   * @param data Optional additional data.
   */
  write(level: LogLevel, message: string, data?: any) {
    if (typeof data !== 'undefined') {
      data = typeof data !== 'object' ? { data } : data
    }
    if (this._handler) {
      this._handler({ level, message, data, space: this._space })
    }
    else {
      // eslint-disable-next-line no-console
      const logFunc = console[level] || console.log
      logFunc.apply(console, data ? [message, data] : [message])
    }
  }

  /**
   * Creates a new Log instance with a sub-namespace.
   * Namespaces are separated by dots (e.g., "global.api.getUser").
   *
   * @param subSpace The sub-namespace to add in camel-case (e.g. `api` and `api.getUser`)
   *
   * @returns A new Log instance with the updated namespace.
   */
  space(subSpace: string) {
    return new Log({
      space: `${this._space}.${subSpace}`,
      handler: this._handler,
    })
  }

  /**
   * Measures the execution time of an asynchronous function and logs a debug message.
   *
   * @param message A message to include in the log output.
   * @param func The asynchronous function to time.
   *
   * @returns A Promise that resolves with the result of the timed function.
   */
  async time<T = any>(message: string, func: () => T | Promise<T>) {
    const start = Date.now()
    const result = await func()
    const end = Date.now()
    this.debug(`${message} took ${end - start}ms`)
    return result
  }

  /**
   * Attaches the log instance to a request object.
   * This is typically used in server middleware.
   *
   * @param req The request object.
   * @param log The Log instance to attach.
   *
   * @example
   * Log.attachToRequest(req, new Log({ space: 'myModule' }))
   *
   * @deprecated
   */
  static attachToRequest(req: any, log: Log) {
    req.$log = log
  }
}
